import React, { useEffect } from 'react'
import { useRoutes, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { CssBaseline, ThemeProvider } from '@material-ui/core'
import './i18n'
import RTL from './components/RTL'
import { fbConfig } from './config'
import useScrollReset from './hooks/useScrollReset'
import useSettings from './hooks/useSettings'
import routes from './routes'
import { createCustomTheme } from './theme'
import analytics from './utils/analytics'
import { AnalyticsProvider } from 'use-analytics'
import useAuth from './hooks/useAuth'
import { ErrorFallback } from './ErrorFallback'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { APP_VERSION, RELEASE_STAGE } from './constants'
import { updateUser } from './actions/userActions'

Bugsnag.start({
	// TODO: Use ENV var (REACT_APP_BUGSNAG_API_KEY ***NOT*** REACT_APP_BUGSNA*P*_KEY)
	apiKey: '89a7584bcfee2e57d9ceba07bed96f18',
	plugins: [new BugsnagPluginReact()],
	enabledReleaseStages: ['production', 'testing'],
	appVersion: APP_VERSION,
	releaseStage: RELEASE_STAGE,
})

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

const App = () => {
	const content = useRoutes(routes)
	const { settings } = useSettings()
	const location = useLocation()
	const { user } = useAuth()

	useScrollReset()

	useEffect(() => {
		const script = document.createElement('script');
		script.src = 'https://www.dwin1.com/81255.js';
		script.defer = true;
		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};

	}, []);

	useEffect(() => {
		// Store AWIN click ID if it exists in URL
		const urlParams = new URLSearchParams(window.location.search);
		const awc = urlParams.get('awc');

		if (awc && !localStorage.getItem('awinClickId')) {
			localStorage.setItem('awinClickId', awc);

			// Set AWIN cookie on server-side
			fetch(`${process.env.REACT_APP_PAYMENTS_URL}/payments/checkout/set-awin-cookie?awc=${awc}`, {
				method: 'GET',
				credentials: 'include' // Include cookies
			}).catch(error => {
				console.error('Error setting AWIN cookie:', error);
			});
		}
	}, []);

	useEffect(() => {
		if (user) {
			const awinClickId = localStorage.getItem('awinClickId');

			if (awinClickId && !user.awinClickId) {
				updateUser(user.id, {
					last_activity: new Date(),
					awinClickId: awinClickId
				});

				localStorage.removeItem('awinClickId');
			} else {
				updateUser(user.id, { last_activity: new Date() });
			}
		}
	}, [location, user])

	const theme = createCustomTheme({
		direction: settings.direction,
		responsiveFontSizes: settings.responsiveFontSizes,
		roundedCorners: settings.roundedCorners,
		theme: settings.theme,
	})

	const errorHandler = (error, info) => {
		console.log("Caught Error: ", error)
		Bugsnag.notify(error)
	}

	return (
		<AnalyticsProvider instance={analytics}>
			<ThemeProvider theme={theme}>
				<ErrorBoundary onError={errorHandler} FallbackComponent={ErrorFallback}>
					<RTL direction={settings.direction}>
						<CssBaseline />
						<Toaster position="bottom-center" />
						{content}
					</RTL>
				</ErrorBoundary>
			</ThemeProvider>
		</AnalyticsProvider>
	)
}

export default App
